// Main modules export file for The Art of Story
import { storyModule01Content } from './modules/story_module_01_StoryLandscape';
import { storyModule02Content } from './modules/story_module_02_WhyStoriesStick';
import { storyModule03Content } from './modules/story_module_03_OscillatingNarratives';
import { storyModule04Content } from './modules/story_module_04_StoryAnatomyContext';
import { storyModule05Content } from './modules/story_module_05_EmotionalCraft';
import { storyModule06Content } from './modules/story_module_06_VulnerabilityCalibration';
import { storyModule07Content } from './modules/story_module_07_StoryPresence';
import { storyModule08Content } from './modules/story_module_08_StoryListening';

// Module metadata and organization - Dive In Modules (Core Storytelling Curriculum)
export const modulesByLevel = {
  foundation: [
    {
      ...storyModule01Content,
      difficulty: 'Foundation',
      order: 1,
      prerequisites: [],
      heroImage: '/images/mod-01-inventory.png',
      category: 'Dive In'
    },
    {
      ...storyModule02Content,
      difficulty: 'Foundation',
      order: 2,
      prerequisites: [],
      heroImage: '/images/mod-02-impact.png',
      category: 'Dive In'
    }
  ],
  essential: [
    {
      ...storyModule03Content,
      difficulty: 'Essential',
      order: 3,
      prerequisites: ['story-landscape'],
      heroImage: '/images/mod-03-oscillating.png',
      category: 'Dive In'
    },
    {
      ...storyModule04Content,
      difficulty: 'Essential',
      order: 4,
      prerequisites: ['why-stories-stick'],
      heroImage: '/images/mod-04-structure.png',
      category: 'Dive In'
    },
    {
      ...storyModule05Content,
      difficulty: 'Essential',
      order: 5,
      prerequisites: ['oscillating-narratives'],
      heroImage: '/images/mod-05-arc.png',
      category: 'Dive In'
    },
    {
      ...storyModule06Content,
      difficulty: 'Essential',
      order: 6,
      prerequisites: ['story-anatomy-context'],
      heroImage: '/images/mod-06-vulnerability.png',
      category: 'Dive In'
    }
  ],
  advanced: [
    {
      ...storyModule07Content,
      difficulty: 'Advanced',
      order: 7,
      prerequisites: ['emotional-craft'],
      heroImage: '/images/mod-07-delivery.png',
      category: 'Dive In'
    },
    {
      ...storyModule08Content,
      difficulty: 'Advanced',
      order: 8,
      prerequisites: ['vulnerability-calibration'],
      heroImage: '/images/mod-08-story-listening.png',
      category: 'Dive In'
    }
  ]
};

// Get all modules as flat array
export const getAllModules = () => {
  return Object.values(modulesByLevel).flat();
};

// Get modules by difficulty level
export const getModulesByDifficulty = (difficulty) => {
  return modulesByLevel[difficulty.toLowerCase()] || [];
};

// Get available modules based on completed modules
export const getAvailableModules = (completedModuleIds = []) => {
  const allModules = getAllModules();

  return allModules.filter(module => {
    // Foundation modules are always available
    if (module.difficulty === 'Foundation') return true;

    // Check if all prerequisites are completed
    if (!module.prerequisites || module.prerequisites.length === 0) return true;

    return module.prerequisites.every(prereqId =>
      completedModuleIds.includes(prereqId)
    );
  });
};

// Get next recommended module based on progress
export const getNextRecommendedModule = (completedModuleIds = []) => {
  const availableModules = getAvailableModules(completedModuleIds);
  const incompleteModules = availableModules.filter(
    module => !completedModuleIds.includes(module.id)
  );

  // Return the lowest order incomplete module
  return incompleteModules.sort((a, b) => a.order - b.order)[0] || null;
};

// Get module by ID
export const getModuleById = (moduleId) => {
  return getAllModules().find(module => module.id === moduleId);
};

// Get module progress percentage
export const getModuleProgress = (moduleId, userProgress = {}) => {
  const moduleData = userProgress[moduleId];
  if (!moduleData) return 0;

  return moduleData.progressPercentage || 0;
};

// Check if module is locked
export const isModuleLocked = (moduleId, completedModuleIds = []) => {
  const module = getModuleById(moduleId);
  if (!module) return true;

  // Foundation modules are never locked
  if (module.difficulty === 'Foundation') return false;

  // Check prerequisites
  if (!module.prerequisites || module.prerequisites.length === 0) return false;

  return !module.prerequisites.every(prereqId =>
    completedModuleIds.includes(prereqId)
  );
};

// Get module statistics
export const getModuleStats = () => {
  const allModules = getAllModules();
  return {
    total: allModules.length,
    foundation: modulesByLevel.foundation.length,
    essential: modulesByLevel.essential.length,
    advanced: modulesByLevel.advanced.length
  };
};

export default {
  modulesByLevel,
  getAllModules,
  getModulesByDifficulty,
  getAvailableModules,
  getNextRecommendedModule,
  getModuleById,
  getModuleProgress,
  isModuleLocked,
  getModuleStats
};
